import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { AppState } from '../../../store';
import { connect } from 'react-redux';
import {
  AvailabilityCalendar,
  Button as KitButton,
  createDateRange,
  EButtonType,
  getNumberOfDaysInConcern,
  parseToApiDate,
} from '@repo/apg-ui';
import { searchActions } from '../../../store/search';
import { productsActions } from '../../../store/products';
import { UiActions } from '../../../store/ui';
import { useNavigate } from 'react-router-dom';
import 'react-date-range/dist/theme/default.css';
import 'react-date-range/dist/styles.css';
import { Modal } from 'react-bootstrap';
import './CalculationModal.scss';
import { generateCriteria } from '../../../utils/payloads';
import { SearchParameters } from '../../../store/search/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { timeslots } from '../../../utils/constants';
import { minutesToSeconds } from '../../../utils/time';
import { createAvailabilityDates } from '../../../utils/calender';

interface Props {
  actionsSearch: typeof searchActions;
  actionsProduct: typeof productsActions;
  uiActions: typeof UiActions;
  show: boolean;
  handleClose: any;
  possibleBookingDates: any;
  industriesBig5: any;
  searchParameters: SearchParameters;
  searchLocations: any;
}

const CalculationModal = ({
  actionsSearch,
  actionsProduct,
  show,
  handleClose,
  possibleBookingDates,
  industriesBig5,
  searchParameters,
  searchLocations,
  searchParameters: {
    dateRanges,
    startDate,
    duration,
    currentSort,
    sort,
    industries,
    timeslot,
    isBudgetSelected,
    isFrequencySelected,
  },
  uiActions,
}: Props) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [availability, setAvailability] = useState<any>([]);
  const [focusedRange, setFocusedRange] = React.useState<[number, number] | undefined>([0, 0]);

  useEffect(() => {
    if (possibleBookingDates?.startdigital?.date && possibleBookingDates?.end?.date) {
      setAvailability(
        createAvailabilityDates(possibleBookingDates?.startdigital?.date, possibleBookingDates?.end?.date),
      );
    }
  }, [possibleBookingDates]);

  const updateTimeslot = (_timeslot) => {
    actionsSearch.setSearchParameters({
      timeslot: _timeslot,
      timeslotInfo: timeslots.filter((id) => id.timeslot === _timeslot)[0],
    });
  };

  const updateBudgetAndDuration = (name, value) => {
    if (name === 'budget') {
      actionsSearch.setSearchParameters({ budget: parseFloat(value) });
    } else if (name === 'frequency') {
      actionsSearch.setSearchParameters({
        frequency: minutesToSeconds(value),
      });
    } else {
      actionsSearch.setSearchParameters({
        spotLength: parseFloat(value),
      });
    }
  };

  const setIsBudgetOrFrequencySelected = (value) => {
    if (value === 'budget') {
      actionsSearch.setSearchParameters({
        isBudgetSelected: true,
        isFrequencySelected: false,
        frequency: 0,
      });
    } else if (value === 'frequency') {
      actionsSearch.setSearchParameters({
        isBudgetSelected: false,
        isFrequencySelected: true,
        budget: 0,
      });
    }
  };
  const updateIndustries = (name, value) => {
    let newIndustries = industries.slice();
    if (value === true) {
      if (!newIndustries.includes(name)) {
        newIndustries.push(name);
      }
    } else {
      const index = newIndustries.indexOf(name);
      if (index !== -1) {
        newIndustries.splice(index, 1);
      }
    }
    actionsSearch.setSearchParameters({
      industries: newIndustries,
    });
  };

  useEffect(() => {
    actionsProduct.fetchIndustriesBig5();
    actionsProduct.fetchBookingDates().then(
      (res: {
        data: {
          length: number;
          start: { date: string | number | Date };
          startdigital: { date: string | number | Date };
        };
      }) => {
        if (res && res.data?.length !== 0) {
          const startDate = new Date(res.data?.startdigital?.date);
          const duration = 7;
          actionsSearch.setSearchParameters({
            dateRanges: [
              createDateRange(startDate, duration, {
                toEndOfYear: false,
                fullWeeks: false,
              }),
            ],
            duration: duration,
          });
        }
      },
    );
  }, [actionsProduct, actionsSearch]);

  const generatePostPayload = (mapPotentialCoordinates?) => {
    const result = {
      sort: [{ column: currentSort, direction: sort[currentSort] }],
      group: ['format', 'subformat'],
      criteria: generateCriteria(searchLocations, searchParameters),
    };

    if (mapPotentialCoordinates) {
      result['mapview'] = mapPotentialCoordinates;
    }
    return result;
  };

  const handleTextClick = (_industriesBig5Id) => {
    const isChecked = industries.includes(industriesBig5[_industriesBig5Id].id);
    updateIndustries(industriesBig5[_industriesBig5Id].id, !isChecked);
  };

  const handleDateChange = (startdate, item) => {
    const newDuration = getNumberOfDaysInConcern(
      item.selection.endDate?.setHours(23, 59, 59, 999),
      startdate?.setHours(0, 0, 0, 0),
    );

    const range = createDateRange(new Date(startdate), newDuration, {
      toEndOfYear: false,
      fullWeeks: false,
    });
    actionsSearch.setSearchParameters({
      startDate: parseToApiDate(startdate),
      duration: newDuration,
      dateRanges: [range],
    });
  };
  return (
    <Modal show={show} onHide={handleClose} backdrop='static' size='xl' keyboard={false}>
      <Modal.Header closeButton className='border-bottom'>
        <Modal.Title>
          <h2>
            <FormattedMessage id='ec.checkout.newCalculation' defaultMessage='Neue Kalkulation starten' />
          </h2>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className='modal-date-timeslot'>
          <h4>
            <FormattedMessage id='ec.checkout.dateAndTimeslot' defaultMessage='Zeitspanne wählen' />
          </h4>
          <div className='explanatoryline'>
            <FormattedMessage
              id='ec.checkout.explanatoryline'
              defaultMessage='Mittels Mausklick auf Ihr gewünschtes Start- und Enddatum im Kalender können Sie die Zeitspanne wählen. Mittels Klick auf die Nummer der Kalenderwoche selektieren Sie zudem eine ganze Woche.'
            />
          </div>

          <AvailabilityCalendar
            availabilities={availability}
            dateRange={[dateRanges[0]]}
            duration={duration}
            isMobile={false}
            isAnalogProduct={false}
            dragSelectionEnabled={false}
            focusedRange={focusedRange}
            onRangeFocusChange={(_focus) => {
              setFocusedRange(_focus);
            }}
            labels={{
              weeks: 'weeks',
              selectWeeksAndChooseDate: 'selectWeeksAndChooseDate',
              available: 'available',
              notAvailable: 'notAvailable',
              disabled: 'disabled',
              note: 'note',
              crossYearErrorText: 'crossYearErrorText',
              days: intl.formatMessage({
                id: 'ec.general.days',
                defaultMessage: 'days',
              }),
              exceptionWeek: 'exceptionWeek',
            }}
            locale={intl.locale}
            possibleBookingDates={possibleBookingDates}
            handleDateChange={handleDateChange}
            showAvailability={false}
          />
          {/*<p>*/}
          {/*    <FormattedMessage*/}
          {/*        id="ec.checkout.selectStartDate"*/}
          {/*        defaultMessage="Select start date."*/}
          {/*    />*/}
          {/*</p>*/}
          {/*<div className="timeslot-tabs d-flex bg-white mt-4">*/}
          {/*    {timeslots &&*/}
          {/*        Object.keys(timeslots).map((_timeslotId, idx) => (*/}
          {/*            <div*/}
          {/*                className={classNames("slot", {*/}
          {/*                    selected:*/}
          {/*                        timeslot ===*/}
          {/*                        timeslots[_timeslotId].timeslot,*/}
          {/*                })}*/}
          {/*                key={`slot-${idx}`}*/}
          {/*                onClick={() => {*/}
          {/*                    updateTimeslot(*/}
          {/*                        timeslots[_timeslotId].timeslot,*/}
          {/*                    );*/}
          {/*                }}*/}
          {/*            >*/}
          {/*                <div className="font-14 font-weight-bold">*/}
          {/*                    {timeslots[_timeslotId].name}*/}
          {/*                </div>*/}
          {/*                <div className="font-12">*/}
          {/*                    {timeslots[_timeslotId].timespan}*/}
          {/*                </div>*/}
          {/*            </div>*/}
          {/*        ))}*/}
          {/*</div>*/}
        </section>
        {/*<section className="modal-budget-duration">*/}
        {/*    <h4>*/}
        {/*        <FormattedMessage*/}
        {/*            id="ec.checkout.budgetAndDuration"*/}
        {/*            defaultMessage="Budget & duration"*/}
        {/*        />*/}
        {/*    </h4>*/}
        {/*    <div className="ms-3 cursor-pointer me-4"></div>*/}
        {/*    <div className="d-flex bg-gray-light p-5 gap-5 align-items-center">*/}
        {/*        <div className="d-flex flex-column w-20 ">*/}
        {/*            <div className="d-flex flex-row">*/}
        {/*                <Input*/}
        {/*                    name="budget-frequency"*/}
        {/*                    value="budget"*/}
        {/*                    type="radio"*/}
        {/*                    radioOptions={[*/}
        {/*                        {*/}
        {/*                            checked: isBudgetSelected,*/}
        {/*                            value: "budget",*/}
        {/*                            onChange: (_, value) => {*/}
        {/*                                setIsBudgetOrFrequencySelected(*/}
        {/*                                    value,*/}
        {/*                                );*/}
        {/*                            },*/}
        {/*                            radioExtraStyles:*/}
        {/*                                "modal-input-radio",*/}
        {/*                        },*/}
        {/*                    ]}*/}
        {/*                    checked={isBudgetSelected}*/}
        {/*                />*/}
        {/*                <div*/}
        {/*                    className="modal-input-radio-label"*/}
        {/*                    onClick={() =>*/}
        {/*                        setIsBudgetOrFrequencySelected("budget")*/}
        {/*                    }*/}
        {/*                >*/}
        {/*                    <FormattedMessage*/}
        {/*                        id="ec.checkout.budget"*/}
        {/*                        defaultMessage="Budget"*/}
        {/*                    />*/}
        {/*                    <Tooltip*/}
        {/*                        content={*/}
        {/*                            <FormattedMessage*/}
        {/*                                id="ec.checkout.tooltip"*/}
        {/*                                defaultMessage="Tooltip"*/}
        {/*                            />*/}
        {/*                        }*/}
        {/*                    >*/}
        {/*                        <KitIcons.InfoIcon className="input-radio-label-tooltip" />*/}
        {/*                    </Tooltip>*/}
        {/*                </div>*/}
        {/*            </div>*/}
        {/*            <div className="d-flex flex-column">*/}
        {/*                <Input*/}
        {/*                    name="budget"*/}
        {/*                    label="Budget"*/}
        {/*                    inputType={EInputType.Text}*/}
        {/*                    type="text"*/}
        {/*                    placeholder="0"*/}
        {/*                    inputWrapperClass="modal-input-text"*/}
        {/*                    onChange={(name, value) =>*/}
        {/*                        updateBudgetAndDuration(name, value)*/}
        {/*                    }*/}
        {/*                    value={searchParameters?.budget || 0}*/}
        {/*                >*/}
        {/*                    <div className="modal-input-text-suffix">*/}
        {/*                        CHF*/}
        {/*                    </div>*/}
        {/*                </Input>*/}
        {/*            </div>*/}
        {/*        </div>*/}
        {/*        <div className="d-flex flex-column w-20">*/}
        {/*            <div className="d-flex flex-row">*/}
        {/*                <Input*/}
        {/*                    name="budget-frequency"*/}
        {/*                    value="frequency"*/}
        {/*                    type="radio"*/}
        {/*                    radioOptions={[*/}
        {/*                        {*/}
        {/*                            checked: isFrequencySelected,*/}
        {/*                            value: "frequency",*/}
        {/*                            onChange: (_, value) =>*/}
        {/*                                setIsBudgetOrFrequencySelected(*/}
        {/*                                    value,*/}
        {/*                                ),*/}
        {/*                            radioExtraStyles:*/}
        {/*                                "modal-input-radio",*/}
        {/*                        },*/}
        {/*                    ]}*/}
        {/*                    checked={isFrequencySelected}*/}
        {/*                />*/}
        {/*                <div*/}
        {/*                    className="modal-input-radio-label"*/}
        {/*                    onClick={() =>*/}
        {/*                        setIsBudgetOrFrequencySelected(*/}
        {/*                            "frequency",*/}
        {/*                        )*/}
        {/*                    }*/}
        {/*                >*/}
        {/*                    <FormattedMessage*/}
        {/*                        id="ec.checkout.frequency"*/}
        {/*                        defaultMessage="Frequency"*/}
        {/*                    />*/}
        {/*                    <Tooltip*/}
        {/*                        content={*/}
        {/*                            <FormattedMessage*/}
        {/*                                id="ec.checkout.tooltip"*/}
        {/*                                defaultMessage="Tooltip"*/}
        {/*                            />*/}
        {/*                        }*/}
        {/*                    >*/}
        {/*                        <KitIcons.InfoIcon className="input-radio-label-tooltip" />*/}
        {/*                    </Tooltip>*/}
        {/*                </div>*/}
        {/*            </div>*/}
        {/*            <div className="d-flex flex-column">*/}
        {/*                <Input*/}
        {/*                    name="frequency"*/}
        {/*                    inputType={EInputType.Text}*/}
        {/*                    label="Frequency"*/}
        {/*                    type="text"*/}
        {/*                    placeholder="0:00"*/}
        {/*                    inputWrapperClass="modal-input-text"*/}
        {/*                    onChange={(name, value) =>*/}
        {/*                        updateBudgetAndDuration(name, value)*/}
        {/*                    }*/}
        {/*                    value={*/}
        {/*                        secondsToMinutes(*/}
        {/*                            searchParameters?.frequency,*/}
        {/*                        ) || null*/}
        {/*                    }*/}
        {/*                >*/}
        {/*                    <div className="modal-input-text-suffix">*/}
        {/*                        <FormattedMessage*/}
        {/*                            id="ec.checkout.minutes"*/}
        {/*                            defaultMessage="Minutes"*/}
        {/*                        />*/}
        {/*                    </div>*/}
        {/*                </Input>*/}
        {/*            </div>*/}
        {/*        </div>*/}
        {/*        <div className="d-flex flex-column w-25 border-start">*/}
        {/*            <div className="d-flex flex-row">*/}
        {/*                <div className="modal-label d-flex align-items-center">*/}
        {/*                    <FormattedMessage*/}
        {/*                        id="ec.checkout.spotLength"*/}
        {/*                        defaultMessage="Spot length"*/}
        {/*                    />*/}
        {/*                    <Tooltip*/}
        {/*                        content={*/}
        {/*                            <FormattedMessage*/}
        {/*                                id="ec.checkout.tooltip"*/}
        {/*                                defaultMessage="Tooltip"*/}
        {/*                            />*/}
        {/*                        }*/}
        {/*                    >*/}
        {/*                        <KitIcons.InfoIcon className="input-radio-label-tooltip" />*/}
        {/*                    </Tooltip>*/}
        {/*                </div>*/}
        {/*            </div>*/}
        {/*            <div className="d-flex flex-column ms-5">*/}
        {/*                <Input*/}
        {/*                    name="spot-length"*/}
        {/*                    inputType={EInputType.Text}*/}
        {/*                    label="Spot length"*/}
        {/*                    type="text"*/}
        {/*                    placeholder="0"*/}
        {/*                    inputWrapperClass="modal-input-text"*/}
        {/*                    onChange={(name, value) =>*/}
        {/*                        updateBudgetAndDuration(name, value)*/}
        {/*                    }*/}
        {/*                    value={searchParameters?.spotLength || null}*/}
        {/*                >*/}
        {/*                    <div className="modal-input-text-suffix">*/}
        {/*                        <FormattedMessage*/}
        {/*                            id="ec.checkout.seconds"*/}
        {/*                            defaultMessage="Seconds"*/}
        {/*                        />*/}
        {/*                    </div>*/}
        {/*                </Input>*/}
        {/*            </div>*/}
        {/*        </div>*/}
        {/*    </div>*/}
        {/*</section>*/}
        {/*<section className="modal-industry">*/}
        {/*    <h4>*/}
        {/*        <FormattedMessage*/}
        {/*            id="ec.checkout.industryOptional"*/}
        {/*            defaultMessage="Industry (optional)"*/}
        {/*        />*/}
        {/*    </h4>*/}
        {/*    <div className="d-flex gap-5">*/}
        {/*        <div className="d-flex industries-wrapper">*/}
        {/*            {industriesBig5 &&*/}
        {/*                Object.keys(industriesBig5).map(*/}
        {/*                    (_industriesBig5Id, idx) => (*/}
        {/*                        <div className="d-flex flex-column modal-input-checkbox-wrapper my-3">*/}
        {/*                            <Input*/}
        {/*                                name={*/}
        {/*                                    industriesBig5[*/}
        {/*                                        _industriesBig5Id*/}
        {/*                                    ].id*/}
        {/*                                }*/}
        {/*                                inputType={EInputType.Checkbox}*/}
        {/*                                label={*/}
        {/*                                    industriesBig5[*/}
        {/*                                        _industriesBig5Id*/}
        {/*                                    ].value*/}
        {/*                                }*/}
        {/*                                type="checkbox"*/}
        {/*                                checked={industries.includes(*/}
        {/*                                    industriesBig5[*/}
        {/*                                        _industriesBig5Id*/}
        {/*                                    ].id,*/}
        {/*                                )}*/}
        {/*                                inputWrapperClass="modal-input-checkbox px-4"*/}
        {/*                                onChange={(name, value) =>*/}
        {/*                                    updateIndustries(*/}
        {/*                                        name,*/}
        {/*                                        value,*/}
        {/*                                    )*/}
        {/*                                }*/}
        {/*                            >*/}
        {/*                                <div*/}
        {/*                                    className="modal-input-checkbox-suffix d-flex"*/}
        {/*                                    onClick={() =>*/}
        {/*                                        handleTextClick(*/}
        {/*                                            _industriesBig5Id,*/}
        {/*                                        )*/}
        {/*                                    }*/}
        {/*                                >*/}
        {/*                                    {*/}
        {/*                                        industriesBig5[*/}
        {/*                                            _industriesBig5Id*/}
        {/*                                        ].value*/}
        {/*                                    }*/}
        {/*                                    <Tooltip*/}
        {/*                                        content={*/}
        {/*                                            <FormattedMessage*/}
        {/*                                                id="ec.checkout.tooltip"*/}
        {/*                                                defaultMessage="Tooltip"*/}
        {/*                                            />*/}
        {/*                                        }*/}
        {/*                                    >*/}
        {/*                                        <div>*/}
        {/*                                            <KitIcons.InfoIcon className="input-radio-label-tooltip" />*/}
        {/*                                            <KitIcons.WhiteInfoIcon className="d-none input-radio-label-tooltip-hover" />*/}
        {/*                                        </div>*/}
        {/*                                    </Tooltip>*/}
        {/*                                </div>*/}
        {/*                            </Input>*/}
        {/*                        </div>*/}
        {/*                    ),*/}
        {/*                )}*/}
        {/*        </div>*/}
        {/*    </div>*/}
        {/*</section>*/}
      </Modal.Body>
      <Modal.Footer className='top-border'>
        <KitButton
          buttonType={EButtonType.PillLarge}
          // isDisabled={!(digiflex.startDate && digiflex.timeslot)}
          onClick={() => {
            handleClose();
            uiActions.setLoading('productRow', true);
            actionsProduct.getFilters();
            navigate('/refine-results');
          }}
        >
          {!(startDate && timeslot) ? (
            <FormattedMessage
              id='ec.checkout.selectDateAndTimeslotFirst'
              defaultMessage='Wählen Sie zuerst ein Datum und ein Zeitfenster'
            />
          ) : (
            <FormattedMessage id='ec.checkout.showResults' defaultMessage='Resultat anzeigen' />
          )}
        </KitButton>
      </Modal.Footer>
    </Modal>
  );
};

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
    ...state.ui,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actionsProduct: bindActionCreators(productsActions as any, dispatch),
    actionsSearch: bindActionCreators(searchActions as any, dispatch),
    uiActions: bindActionCreators(UiActions as any, dispatch),
    dispatch: dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CalculationModal);
